import {
  type SiteResponse,
  SiteType,
  v1SitesOfferSetDetail,
} from '@on3/api/src';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { useModal } from '@on3/ui-lib/components/Modal/Provider';
import { SelectSearch } from '@on3/ui-lib/components/SelectSearch';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { Join } from '@on3/ui-lib/components/Subscription/Templates/Join';
import { externalApi, useSite } from '@on3/ui-lib/index';
import { SiteKeys } from '@on3/ui-lib/utils/constants';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { createTitle } from '@public/utils/meta';
import { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './JoinTeamSelect.module.scss';

interface JoinTeamSelectProps {
  site: SiteResponse | null;
  sites: SiteResponse[];
  select: (team: SiteResponse | null) => void;
}

const OffersLoading = () => (
  <>
    <div className={styles.siteBranding}>
      <div className={styles.loading} />
    </div>
    <div className={styles.offers}>
      <div className={styles.loading} />
    </div>
  </>
);

export const JoinTeamSelect = ({
  site,
  select,
  sites: _sites,
}: JoinTeamSelectProps) => {
  const { currentSite } = useSite();
  const { showModal } = useModal();
  const [fetchingOffers, setFetchingOffers] = useState(false);
  const { setOfferSet, setOfferSetSite } = useSubscribe();

  const isOn3National = currentSite?.isNational && currentSite?.key === 44;

  useEffect(() => {
    if (!site) return;
    setFetchingOffers(true);

    v1SitesOfferSetDetail(externalApi, { siteKey: site.key })
      .then((r) => setOfferSet(r))
      .catch(() => console.error('Error fetching offer set'))
      .finally(() => setFetchingOffers(false));
  }, [site, setOfferSet]);

  const sites = useMemo(() => {
    const groups = [
      {
        types: [SiteType.Team, SiteType.Channel],
        group: 'College',
        options: _sites
          .filter(
            (s) => s.type === SiteType.Team || s.type === SiteType.Channel,
          )
          // filter all channel sites that have a sibling key
          .filter((s) =>
            s.type === SiteType.Channel && s.siblingSiteKey ? false : true,
          )
          // special filtering logic
          .filter((s) => {
            // exclude site keys 1372, 1373, 1374
            const excludedKeys = [
              SiteKeys.KSR,
              SiteKeys.CatsIllustrated,
              SiteKeys.MaizeAndBlueReview,
              SiteKeys.InsideNebraska,
              SiteKeys.OUInsider,
            ];

            return !excludedKeys.includes(s.key);
          })
          .map((s) => ({
            ...s,
            label: !s.organization
              ? s.name || s.siteName || ''
              : createTitle(
                  s.organization?.knownAs || s.organization?.name,
                  s.organization?.mascot,
                  [74, 453].includes(s.key) ? `- ${s.name || s.siteName}` : '',
                ),
            avatarUrl: getAssetUrl(s.organization?.defaultAssetRes),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        types: [SiteType.National],
        group: 'National',
        options: _sites
          .filter((s) => s.type === SiteType.National)
          .map((s) => ({
            ...s,
            label: s.name || s.siteName || 'Unknown',
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        types: [SiteType.HighSchool],
        group: 'High School',
        options: _sites
          .filter((s) => s.type === SiteType.HighSchool)
          .map((s) => ({
            ...s,
            label: s.name || s.siteName || 'Unknown',
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
    ];

    return groups;
  }, [_sites]);

  const handleSearchChange = useCallback(
    (o: SiteResponse) => {
      select(o);
      setOfferSetSite(o);
    },
    [select, setOfferSetSite],
  );

  const selectedSite =
    sites.flatMap((g) => g.options).find((s) => s.key === site?.key) || null;
  const showOffers = !!site;

  if (!isOn3National) return null;

  return (
    <section className={styles.block}>
      <header className={styles.header}>
        <h2>Search for a team or site to get started</h2>
      </header>
      <SelectSearch
        label="Search..."
        onChange={handleSearchChange}
        options={sites}
        value={selectedSite}
      />
      {showOffers && (
        <div className={styles.offersContainer}>
          {fetchingOffers ? (
            <OffersLoading />
          ) : (
            <>
              {site?.alternateLogo && (
                <div className={styles.siteBranding}>
                  <Avatar
                    className={styles.logo}
                    height={40}
                    source={getAssetUrl(site.alternateLogo)}
                    teamAvatar
                  />
                </div>
              )}
              <div className={styles.offers}>
                <Join
                  handleJoin={(props) =>
                    showModal('user.join', props, 'screen')
                  }
                />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
